
import VueApp from '@/@types/app/VueApp';
import articleEditService from '@/services/article/ArticleEditService';
import { dispatchLoadArticleComments } from '@/store/dispatchers/articleCommentsDispatchers';
import { Component, Watch } from 'vue-property-decorator';
import { Article } from '@/models/article/Article';
import ArticleEditor from '@/components/article-editor/ArticleEditor/ArticleEditor.vue';
import { ARTICLE_EDIT, DRAFT_EDIT, HOME } from '@/routerNames';
import UploadRecordStatus from '@/components/layout/shared/UploadRecordStatus/UploadRecordStatus.vue';
import { Draft } from '@/models/article/Draft';
import { ArticleEditMode } from '@/@types/ArticleEditMode';
import { ElLoadingComponent } from 'element-ui/types/loading';
import {
  commitSetArticleEditorActiveTextTabId,
  commitSetArticleEditorArticle,
} from '@/store/commits/articleEditorCommits';
import { commitSetArticleMode } from '@/store/commits/sharedCommits';
import { Route } from 'vue-router/types/router';
import { dispatchLoadCompanyUsers } from '@/store/dispatchers/companyDispatchers';
import { dispatchDisableOcrMode } from '@/store/dispatchers/uiDispatchers';
import draftService from '@/services/draft/DraftService';
import { watchPendingExtensionUpload } from '@/services/article/watchPendingExtensionUpload';

@Component({
  components: {
    ArticleEditor,
    UploadRecordStatus,
  },
})
export default class ArticleEditPage extends VueApp {
  loader: ElLoadingComponent = null;
  article: Article = null;

  get isVideoProcessing() {
    return this.$store.state.articleEditor.isVideoProcessing;
  }
  get articleEditMode() {
    return this.$store.state.articleMode;
  }

  async loadArticle(articleId: string) {
    this.loader = this.$loading({ lock: true, text: 'Loading Article' });

    try {
      const isDraft = this.$route.name === DRAFT_EDIT;
      let article: Article;
      if (isDraft) {
        const res = await this.$api.draft.get(articleId);
        article = Article.fromJson(res.data.draft);
      } else {
        const res = await this.$api.article.get(articleId);
        article = Article.fromJson(res.data.article);
      }

      commitSetArticleEditorArticle(article);
      this.article = article;
    } catch (res) {
      this.$api.handleResponseError(res);
      return this.$router.push({ name: HOME });
    } finally {
      if (this.loader) {
        this.loader.close();
        this.loader = null;
      }
    }
  }

  loadDraftArticle(id: string) {
    this.$nextTick(() => {
      this.loader = this.$loading({
        lock: true,
        text: 'Loading Article',
      });
    });

    this.$api.article
      .get(id, true)
      .then((res: any) => {
        const article = Draft.fromJson(res.data.article);
        commitSetArticleEditorArticle(article);
        this.article = article;
      })
      .catch((res: any) => {
        this.$api.handleResponseError(res);
        return this.$router.push({ name: HOME });
      })
      .then(() => {
        if (this.loader) {
          this.loader.close();
          this.loader = null;
        }
      });
  }

  async init() {
    const routeName = this.$route.name;
    const articleId = this.$route.params.id;
    const isLoadDraftArticle = articleId && routeName === DRAFT_EDIT;
    this.$store.commit('toggleExpandMainSidebar', false);

    this.updateEditMode();

    if (isLoadDraftArticle) {
      this.loadDraftArticle(articleId);
    } else if (routeName === ARTICLE_EDIT) {
      await this.loadArticle(articleId);
    } else {
      if (this.$store.state.createArticle) {
        this.article = Article.fromJson(this.$store.state.createArticle);
        this.$store.commit('setCreateArticle', null);
      } else {
        this.article = new Article();
      }
    }

    if (this.articleEditMode === ArticleEditMode.NOTES && !this.article.textTabs.length) {
      commitSetArticleMode(ArticleEditMode.VIDEO);
      commitSetArticleEditorActiveTextTabId(null);
    }

    if (this.articleEditMode === ArticleEditMode.NOTES && this.article.textTabs.length) {
      articleEditService.setActiveTextTab({
        article: this.article,
        textTabId: this.article.textTabs[0].id,
      });
    }

    await dispatchLoadArticleComments();

    await draftService.loadDraftHash(articleId);
  }

  created() {
    watchPendingExtensionUpload(this, () => this.article);
    this.init();

    if (!this.$store.state.companyUsers.length) {
      dispatchLoadCompanyUsers();
    }
  }

  beforeDestroy() {
    if (this.loader) {
      this.loader.close();
    }
    commitSetArticleEditorActiveTextTabId(null);
    this.$store.commit('setArticleEditorIsVideoProcessing', false);
    dispatchDisableOcrMode();
  }

  updateEditMode() {
    const mode = this.$route.params.mode === ArticleEditMode.NOTES ? ArticleEditMode.NOTES : ArticleEditMode.VIDEO;
    commitSetArticleMode(mode);
  }

  @Watch('$route', { immediate: true, deep: true })
  onUrlChange(route: Route, oldRoute: Route) {
    if (oldRoute && route.params.id !== oldRoute.params.id) {
      return this.init();
    }

    this.updateEditMode();

    this.$store.dispatch('commentsSidebarCheckAutoShowComment');
  }
}
